import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { XMarkIcon, MagnifyingGlassIcon, SpeakerWaveIcon } from '@heroicons/react/24/outline';
import { IAudioGeneration, AudioGenerationStatus, IUserModel, PaginationQuery, AudioGenerationQuery } from '@packages/global';
import { audioGenerationApi } from '../../services/user/audioGeneration.api';
import Pagination from '../Pagination';
import AudioPlayer from './AudioPlayer';

interface AudioSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (audio: IAudioGeneration) => void;
  models?: IUserModel[]; // Made optional since we're not using it anymore
}

export default function AudioSelector({ isOpen, onClose, onSelect }: AudioSelectorProps) {
  const { t } = useTranslation(['common_ui', 'common']);
  const [audios, setAudios] = useState<IAudioGeneration[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedAudio, setSelectedAudio] = useState<IAudioGeneration | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0, page: 1, limit: 24 });

  const loadAudios = async (page: number = 1) => {
    setLoading(true);
    try {
      const queryData: PaginationQuery<AudioGenerationQuery> = {
        query: {
          search: search || undefined,
          status: AudioGenerationStatus.COMPLETED
        },
        pagination: {
          page,
          limit: 15,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        }
      };
      const response = await audioGenerationApi.audioGenerationPageList(queryData);
      setAudios(response.items);
      setPagination(response.pagination);
      setCurrentPage(page);
    } catch (error) {
      console.error('加载音频失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedAudio(null);
      setCurrentPage(1);
      loadAudios(1);
    }
  }, [isOpen, search]);

  const handleSelect = () => {
    if (selectedAudio) {
      onSelect(selectedAudio);
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[90]">
      <div className="bg-bg-surface rounded-lg w-[800px] h-[600px] flex flex-col border border-border-subtle shadow-2xl">
        <div className="flex items-center p-4 border-b border-border-subtle">
          <h2 className="text-xl font-semibold text-gray-50">{t('common_ui:selector.audio.title')}</h2>
          <div className="flex items-center gap-3 ml-auto">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('common_ui:selector.audio.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-80 pl-9 pr-4 py-1.5 border border-border-subtle rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-bg-form text-gray-50 placeholder-gray-500 text-sm"
              />
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-200 cursor-pointer">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : audios.length === 0 ? (
            <div className="text-center text-gray-400 py-12">
              <div className="text-6xl mb-4">🎵</div>
              <p>{t('common_ui:selector.audio.empty')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {audios.map((audio) => (
                <div
                  key={audio.id}
                  onClick={() => setSelectedAudio(audio)}
                  className={`relative cursor-pointer rounded-lg p-3 border-2 transition-all ${selectedAudio?.id === audio.id
                    ? 'border-blue-500 ring-2 ring-blue-400 bg-blue-50/5'
                    : 'border-border-subtle hover:border-border-default bg-bg-elevated/50'
                    }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      <SpeakerWaveIcon className="h-5 w-5 text-gray-400" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-xs font-medium text-gray-50 leading-tight" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {audio.prompt.length > 40 ? `${audio.prompt.substring(0, 40)}...` : audio.prompt}
                        </h3>
                        {selectedAudio?.id === audio.id && (
                          <div className="bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs ml-2 flex-shrink-0">✓</div>
                        )}
                      </div>

                      {audio.resultAudioUrl && (
                        <AudioPlayer
                          src={audio.resultAudioUrl}
                          className="w-full h-6 dark:[filter:invert(1)_hue-rotate(180deg)]"
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t border-border-subtle bg-bg-elevated">
          <div className="flex-1">
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={loadAudios}
              showInfo={false}
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-300 bg-bg-surface border border-border-subtle rounded-lg hover:bg-bg-elevated cursor-pointer transition-colors"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedAudio}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              {t('common_ui:selector.audio.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
